import { createEffect, Show } from 'solid-js'
import { cn } from '../utils/cn.ts'

export type TFindMode = 'find' | 'goto'

/*
  code search has `f` for find-in-file and `g` for go-to-line as two separate things.
  they're one strip here because they occupy the same corner and are never open at
  once, and because the only real difference is what enter does with what you typed
*/
export function FindBar(props: {
  mode: TFindMode
  value: string
  total: number
  current: number
  onInput: (value: string) => void
  onSubmit: (backwards: boolean) => void
  onClose: () => void
}) {
  let input!: HTMLInputElement

  // reopening on a different mode (or a second `f`) should put the caret back in the
  // box and select what's there, so typing replaces the old query
  createEffect(() => {
    if (props.mode) {
      input.focus()
      input.select()
    }
  })

  return (
    <div class="flex shrink-0 items-center gap-2 border-b border-border bg-muted/60 px-2 py-1 text-xs">
      <div class={cn('size-3.5 shrink-0 text-muted-foreground', props.mode === 'find' ? 'i-lucide-text-search' : 'i-lucide-corner-down-right')} />
      <input
        ref={input}
        value={props.value}
        placeholder={props.mode === 'find' ? 'find in file' : 'go to line'}
        inputMode={props.mode === 'goto' ? 'numeric' : 'text'}
        class="min-w-0 flex-1 bg-transparent font-mono outline-none placeholder:text-muted-foreground"
        spellcheck={false}
        autocomplete="off"
        onInput={e => props.onInput(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            props.onSubmit(e.shiftKey)
          }
          if (e.key === 'Escape') {
            e.preventDefault()
            props.onClose()
          }
        }}
      />

      <Show when={props.mode === 'find' && props.value}>
        <span class="shrink-0 text-muted-foreground tabular-nums">
          <Show when={props.total > 0} fallback="no matches">
            {props.current + 1}
            {' / '}
            {props.total}
          </Show>
        </span>
        <div
          class="i-lucide-chevron-up size-3.5 shrink-0 cursor-pointer text-muted-foreground hover:text-foreground"
          onClick={() => props.onSubmit(true)}
        />
        <div
          class="i-lucide-chevron-down size-3.5 shrink-0 cursor-pointer text-muted-foreground hover:text-foreground"
          onClick={() => props.onSubmit(false)}
        />
      </Show>

      <div
        class="i-lucide-x size-3.5 shrink-0 cursor-pointer text-muted-foreground hover:text-foreground"
        onClick={() => props.onClose()}
      />
    </div>
  )
}
